/**
 * String type-guard predicates. Pure-leaf module: NO value imports. Lives in `const/` so pure `utils/` leaves (which
 * may import `@federa/const` leaves but NOT a `@federa/utils` sibling) can value-import these zero-dependency guards.
 */

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic guard homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. True for a truthy string. Unlike
 * isNonEmptyString this does NOT trim whitespace first, so a whitespace-only string passes; use it when
 * whitespace-only strings are valid inputs.
 *
 * THE HAND-ROLLED SPELLING TO ADOPT HERE IS `typeof x === 'string' && x.length > 0` used as a BOOLEAN -- an `if`
 * test, a boolean return, or a `.filter` type predicate. It agrees with this body on every input, and the whole
 * conjunction collapses to a bare `isValidString` reference when it is the entire predicate. Its TERNARY sibling
 * `typeof x === 'string' && x.length > 0 ? x : null` is a different helper -- {@link nonEmptyStringOrNull}, whose
 * paired forbid-rule is `QUAL-INLINE-NON-EMPTY-STRING-COERCE-001`.
 */
export function isValidString(value: unknown): value is string {
	return !!value && typeof value === 'string';
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic first-occurrence claim homed in const/ so pure utils/
 * leaves can value-import it. `value` when it is a truthy string ({@link isValidString}) not yet in `seen` -- recorded
 * there as it is claimed -- else null. The keep-the-first-occurrence dedupe of a string key that the UserDO property
 * feed and the MM-cancellation account list each spelled as `isValidString(x) && !seen.has(x)` then `seen.add(x)`.
 * Answers the VALUE rather than a boolean so the caller keeps a `string` to push: a boolean predicate here would lie
 * on its false branch, which covers a present-but-seen string too.
 */
export function claimUnseenString(seen: Set<string>, value: unknown): string | null {
	if (!isValidString(value) || seen.has(value)) return null;
	seen.add(value);
	return value;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic guard homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. True for a string with non-whitespace
 * content -- non-empty AFTER trimming. Contrast {@link isValidString}, which does NOT trim and therefore
 * admits a whitespace-only string.
 */
export function isNonEmptyString(value: unknown): value is string {
	return typeof value === 'string' && value.trim().length > 0;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the list form of {@link isNonEmptyString} -- an array whose every
 * member has content, the empty array included. The `Array.isArray(v) && v.every(isNonEmptyString)` guard the
 * mechanical line-edit and rule-retire hosts each spelled on their list inputs (2026-09-28 fold).
 */
export function isNonEmptyStringArray(value: unknown): value is string[] {
	return Array.isArray(value) && value.every(isNonEmptyString);
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): a value that is absent (null) or has content: what an optional text input must be, so a caller that refuses the
 *  rest reads the value as `string | null` after the guard -- the rule-retire door's ground and the dispatch ledger's
 *  worktree path (2026-09-28 fold). `undefined` is neither: a dropped member is not a stated absence. */
export function isNonEmptyStringOrNull(value: unknown): value is string | null {
	return value === null || isNonEmptyString(value);
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): the first capture of a match, trimmed; null for a miss -- the
 *  `<KEY>: <value>` line read the sweep discharge and the draft marker each spelled (2026-09-28 fold). An unset group
 *  reads as the empty string, as {@link trimToEmpty} reads it. */
export function trimmedFirstGroupOrNull(match: RegExpMatchArray | null): string | null {
	return match === null ? null : trimToEmpty(match[1]);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coalescer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. The trimmed string when it has content,
 * else null (non-strings included).
 */
export function trimToNull(value: unknown): string | null {
	if (typeof value !== 'string') return null;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link trimToNull} that ALSO answers null for the literal text
 * `null` in any case -- the one input cleaner `cleanInput` (`utils/data/clean.ts`) and the user-id cleaners
 * (`utils/strings/user-id.ts`) share. Homed in const/ because the utils/ floor bars those two leaves from importing
 * each other; each had spelled the trim-and-null-literal decision for itself. The literal `undefined` stays text.
 */
export function trimToNullDroppingNullLiteral(value: unknown): string | null {
	const trimmed = trimToNull(value);
	return trimmed !== null && trimmed.toLowerCase() === 'null' ? null : trimmed;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coalescer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. The OPTIONAL-FIELD twin of {@link trimToNull},
 * identical in every input it accepts and rejects and differing ONLY in the absent value: `undefined` rather than
 * `null`, for narrowing an untrusted payload onto an interface whose property is `field?: string`, where `null` would
 * not be assignable. Contrast {@link asStringOrUndefined}, which shares the return type but does NOT trim and so lets
 * `''` and whitespace-only strings through.
 *
 * Homed here rather than in a shared utils/ leaf because the utils/ import floor
 * (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist ever) forbids one: a utils/ leaf cannot value-import a utils/
 * sibling, so every pure consumer under `utils/market-maker/` reaches its shared helpers through `@federa/const`.
 * Consumers: the `utils/market-maker/mm-*` extra parsers plus the `flows/mm-*` recorder leaves.
 */
export function trimToUndefined(value: unknown): string | undefined {
	const trimmed = trimToNull(value);
	return trimmed === null ? undefined : trimmed;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coalescer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. The EMPTY-STRING twin of {@link trimToNull} and
 * {@link trimToUndefined}, identical in every input it accepts and rejects and differing ONLY in the absent value:
 * `''` rather than `null`/`undefined`, for a site that immediately does string work on the result (`.length`, a `===`
 * against another trimmed string, template interpolation) and wants absence to behave as empty rather than branch.
 *
 * THE TWO HAND-ROLLED SPELLINGS IT REPLACES ARE `(x ?? '').trim()` AND `x?.trim() ?? ''`. They agree with this body
 * and with each other on every `string | null | undefined` input, which is why one contract had grown two spellings
 * across the tree with no SSOT to converge on. PAIRED FORBID-RULE: the `trimToEmpty` row of `SSOT_IDIOM_MANIFEST`
 * (`const/ssot-idiom-index.ts`; the row is in `const/ssot-idiom-text.rows.ts`), whose two `patternSources` are exactly
 * those spellings.
 *
 * IT IS TOTAL WHERE BOTH INLINE SPELLINGS ARE PARTIAL, and that is the one input class where adopting it is a
 * behaviour CHANGE rather than a fold: on a non-string (an `any`-typed receiver carrying a number off a D1 column)
 * `(x ?? '').trim()` and `x?.trim() ?? ''` both THROW a TypeError, while this answers `''`. Every adoption is
 * therefore owed a receiver-type check, and a site whose receiver is not `string | null | undefined` is classified
 * before it is converted rather than swept.
 *
 * NOT {@link toSafeString}, the neighbour it is closest to and the one it keeps getting confused with: that one
 * renders EVERY input through `String()` before trimming, so absence becomes the literal `'null'` / `'undefined'`
 * text rather than `''`, and a length check on its result reads 4 or 9 where this one reads 0. Nor is it
 * `stringOrEmpty(x).trim()` spelled in one call -- {@link stringOrEmpty} does not trim, so composing the two is this
 * body written out longhand and is the shape the forbid-rule's cure points here from.
 */
export function trimToEmpty(value: unknown): string {
	return trimToNull(value) ?? '';
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. COERCER, not a guard: a truthy-length string
 * passes through, anything else (wrong type, `''`) becomes `null` -- the shape a D1 write / JSON payload wants,
 * where the absent case must be an explicit SQL NULL rather than an empty string. Like {@link isValidString} and
 * UNLIKE {@link isNonEmptyString} and {@link trimToNull}, it does NOT trim, so a whitespace-only string passes
 * through verbatim.
 */
export function nonEmptyStringOrNull(value: unknown): string | null {
	return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the first candidate {@link nonEmptyStringOrNull} accepts, in order,
 * VERBATIM, or `null` when none does -- a preferred source with its fallbacks (a bank account before its chart code,
 * the run's own id before the host reading's). The untrimmed twin of {@link firstTrimmedNonEmptyString}: same variadic
 * values and `null` sentinel, but a whitespace-only candidate wins and no winner is rewritten, so a key read through it
 * stays in the key space its untrimmed siblings write. The record-keyed reader `firstNonEmptyString`
 * (`const/dual-spelling.ts`) reads through it.
 */
export function firstUntrimmedNonEmptyString(...candidates: readonly unknown[]): string | null {
	for (const candidate of candidates) {
		const value = nonEmptyStringOrNull(candidate);
		if (value !== null) return value;
	}
	return null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. The OPTIONAL-FIELD twin of
 * {@link nonEmptyStringOrNull} -- identical in every input it accepts and rejects, differing ONLY in the absent
 * value: `undefined` rather than `null`, for narrowing an untrusted payload onto an interface whose property is
 * `field?: string`, where `null` would not be assignable. Like its twin it does NOT trim, so a whitespace-only
 * string passes through verbatim.
 *
 * IT IS NOT {@link asStringOrUndefined} AND NOT {@link trimToUndefined}, the two neighbours it kept getting
 * hand-rolled between. `asStringOrUndefined` shares the return type but carries NO length guard, so it passes `''`
 * through; `trimToUndefined` shares the length guard but TRIMS both the test and the returned value, so adopting it
 * at a site that wants the original bytes silently rewrites them. Before this existed the shape had no SSOT at all,
 * and `QUAL-INLINE-NON-EMPTY-STRING-COERCE-001` deliberately pinned its matcher to the `: null` arm because firing
 * on the `: undefined` arm would have prescribed a helper that was not in the tree; that rule now governs both arms.
 */
export function nonEmptyStringOrUndefined(value: unknown): string | undefined {
	const nonEmpty = nonEmptyStringOrNull(value);
	return nonEmpty === null ? undefined : nonEmpty;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. Non-string becomes `undefined` -- the
 * OPTIONAL-FIELD shape, for narrowing an untrusted payload onto an interface whose property is `field?: string`.
 * EMPTY STRING PASSES THROUGH, unlike {@link nonEmptyStringOrNull}: at a wire boundary `''` is a value the sender
 * actually sent, and collapsing it would silently drop a deliberate clear-this-field write.
 */
export function asStringOrUndefined(value: unknown): string | undefined {
	return typeof value === 'string' ? value : undefined;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. A string with non-whitespace content, returned
 * VERBATIM; everything else -- wrong type, `''`, whitespace-only -- becomes null.
 *
 * THE TRIM SPLIT IS THE WHOLE POINT AND IT IS ASYMMETRIC: the TEST trims, the RETURN does not. That is what separates
 * it from both neighbours it keeps getting hand-rolled between. {@link trimToNull} applies the same test and returns
 * the TRIMMED string, so adopting it at a site that wants the original silently rewrites the value; {@link
 * nonEmptyStringOrNull} returns verbatim but tests with `.length > 0`, so it admits `'   '` -- a whitespace-only id
 * reaching a D1 bind and matching nothing, which is the defect the trimming test exists to prevent. Neither
 * substitutes, and four call sites had independently written this third combination out by hand.
 */
export function nonBlankStringOrNull(value: unknown): string | null {
	return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. The EMPTY-STRING twin of
 * {@link nonBlankStringOrNull}, identical in every input it accepts and rejects and differing ONLY in the absent
 * value: `''` rather than `null`, for a site that immediately does string work on the result (a `values` array bound
 * to a D1 `TEXT NOT NULL` column, a template interpolation, a parser that takes `string` and not `string | null`)
 * and wants absence to behave as empty rather than branch.
 *
 * IT KEEPS THE ASYMMETRIC TRIM SPLIT ITS NULL TWIN ARGUES AT LENGTH -- the TEST trims, the RETURN does not -- and
 * that is what separates it from BOTH neighbours it is otherwise between. {@link trimToEmpty} applies the same test
 * and returns the TRIMMED string, so adopting it at a site that wants the original bytes silently rewrites them;
 * {@link stringOrEmpty} returns verbatim but carries no length test at all, so it passes `'   '` through where this
 * one answers `''`. Neither substitutes, and six call sites in ONE leaf had written this third combination out by
 * hand as `isNonEmptyString(x) ? x : ''`.
 *
 * THE BODY IS SPELLED AS A GUARD-CALL TERNARY, DELIBERATELY, and that is load-bearing rather than stylistic:
 * `QUAL-GUARD-COERCER-HALF-ADOPTION-001` DERIVES its pair table by parsing coercer declarations of exactly this shape
 * (`return <guard>(value) ? value : <fallback>;`) out of this leaf and `const/record-guards.ts` at scan time, so this
 * spelling is what arms the paired forbid-rule for the half-adoption it exists to cure. Re-spelling it as an inline
 * `typeof` conjunction -- which is what its null twin above still does, and the recorded reason that rule's own
 * statusReason named the string family unclaimed -- disarms the rule with no gate to notice.
 */
export function nonBlankStringOrEmpty(value: unknown): string {
	return isNonEmptyString(value) ? value : '';
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. The NULL twin of {@link asStringOrUndefined},
 * identical in every input it accepts and rejects and differing ONLY in the absent value -- `null` rather than
 * `undefined`, for a D1 bind or JSON payload where the absent case must be an explicit SQL NULL.
 *
 * EMPTY STRING PASSES THROUGH, which is what separates it from {@link nonEmptyStringOrNull}: at a wire boundary `''`
 * is a value the sender actually sent, and collapsing it would silently drop a deliberate clear-this-field write.
 * Reach for `nonEmptyStringOrNull` only where empty and absent genuinely mean the same thing.
 *
 * Relocated here from a single-export `utils/audit/` leaf, which the utils floor
 * (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist) put out of reach of its own utils-layer callers -- so
 * the former utils wrangler JSONC walk (since carved to `const/wrangler-jsonc-env-walk.ts`) carried a private copy alongside a second one in
 * `tools/ledger/`. `QUAL-CROSS-FILE-FN-BODY-DUP-001` grouped all three.
 */
export function stringOrNull(value: unknown): string | null {
	return typeof value === 'string' ? value : null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. RENDERS a scalar onto a string column: a number
 * becomes its string rendering, a string passes through, and only genuine absence (`null`/`undefined`) becomes null.
 *
 * THE POINT IS THE NUMBER ARM, which is what separates it from {@link stringOrNull} -- that one answers null for a
 * number, i.e. it DROPS the value. The title source schemas declare every identifier/party slot
 * `['string','number','null']` because recorder and clerk portals render instrument numbers and party columns
 * numerically, so a `stringOrNull` at those seams would silently discard a real recording ref. Consumers: the recorder
 * and court instrument mappers, whose targets (`InstrumentInput.recordingRef` and siblings) are `string | null`.
 *
 * THE PARAMETER IS `unknown`, the same shape {@link coerceString} carries: the const coercer family takes what a
 * runtime seam actually hands it. A narrower scalar union forecloses its own adoption at a seam whose fields are typed
 * `unknown` (a D1 row read), where reaching the SSOT would need a CAST at the call site asserting a shape the row does
 * not guarantee -- and a cast bought to reach an SSOT is a worse defect than the respelling. The RENDERING is deliberately total for every
 * non-nullish value: at a D1 insert, dropping an unexpected value to null is the silent-data-loss direction, which is
 * exactly what the number-arm paragraph above exists to prevent.
 */
export function scalarToStringOrNull(value: unknown): string | null {
	return value === null || value === undefined ? null : String(value);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. TOTAL coercion -- always returns a string,
 * with `''` for every non-string. For call sites that immediately do string work (`.includes`, `.split`, template
 * interpolation) on a field of unknown runtime type and want the absent case to behave as empty rather than
 * branch. Prefer {@link asStringOrUndefined} where absent and empty must stay distinguishable.
 */
export function stringOrEmpty(value: unknown): string {
	return typeof value === 'string' ? value : '';
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): TOTAL coercion that STRINGIFIES -- a string passes through, and
 * everything else becomes `String(value ?? '')`, so `null`/`undefined` yield `''` and every other value yields its
 * string rendering. The SSOT for the flatten-an-unknown-identity-claim-to-string pattern that recurred across the auth
 * helpers.
 *
 * NOT {@link stringOrEmpty}, and the difference is the whole reason both exist: that one DISCARDS a non-string
 * (a number `42` becomes `''`), this one RENDERS it (`'42'`). At an identity seam where an id may arrive as a number
 * off a D1 column, picking the discarding sibling silently empties a real value. It also differs from
 * {@link toSafeString}, which does NOT trim here and, crucially, renders absence as the literal `'null'` /
 * `'undefined'` rather than `''`.
 *
 * NOT a twin of `coerceDisplayString` (`utils/data/coerce-display-string.ts`), and the merge is DECLINED at both
 * leaves: that one refuses objects and returns a display sentinel for a reader, this one stringifies anything for a
 * machine. The `Display` qualifier was added to the other side in 2026-08 precisely so a bare call site names its
 * contract.
 *
 * HOMED ON THE CONST FLOOR, the cure `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` arm (1) prescribes and that
 * `const/sql-comment-strip.ts` and `const/balanced-delim-scan.ts` record: the body has ZERO imports, and a `utils/` home
 * would make it unreachable from every `utils/` leaf, which is where hand-rolled respellings grow -- on money paths
 * among them. A domain-named delegation wrapper over it is the shape `QUAL-CONST-DELEGATION-WRAPPER-001` refuses, so
 * every consumer imports this export directly.
 *
 * PAIRED FORBID-RULE: `QUAL-COERCE-STRING-NULLISH-DEFAULT-INLINE-001`, which bans the inline
 * `typeof v === 'string' ? v : String(v ?? '')` respelling in its property-receiver and bare-identifier spellings AND
 * -- since the arm added 2026-09-05 -- the bare `String(v ?? '')` call that agrees with this body on every input.
 * ALL THREE arms carry a `pathExcludePattern` for THIS leaf, so the declaration below is the one legitimate carrier of
 * the shape.
 */
export function coerceString(value: unknown): string {
	return typeof value === 'string' ? value : String(value ?? '');
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): each row's `id` through {@link coerceString}, in row order -- the
 * id list a set-based SQL read binds as a JSON array. The My-Auctions source ids and the runner-up banc ids each
 * spelled `rows.map((row) => coerceString(row.id))`; a caller that must drop empty ids filters the result.
 */
export function idStringsOf(rows: readonly { readonly id?: unknown }[]): string[] {
	return rows.map((row) => coerceString(row.id));
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coalescer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. The first argument satisfying
 * {@link isNonEmptyString} -- a string with non-whitespace content -- returned TRIMMED; null when none qualifies.
 *
 * THE NAME CARRIES `Trimmed` DELIBERATELY, because `const/dual-spelling.ts` exports a same-shaped coalescer whose
 * three defining choices are all the opposite: it takes a RECORD plus key names rather than values, it does NOT trim
 * (its docstring records that as a deliberate behaviour-preservation decision), and it returns `undefined` rather
 * than `null`. The two were authored a day apart from opposite directions and briefly shared a bare
 * `firstNonEmptyString` name, so an author reaching for the wrong leaf silently changed both whitespace handling and
 * the absent-value sentinel. Do not rename this back to the bare form.
 */
export function firstTrimmedNonEmptyString(...values: unknown[]): string | null {
	for (const value of values) {
		if (typeof value === 'string' && value.trim().length > 0) return value.trim();
	}
	return null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic token scan homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. True when `haystack` contains ANY token as a
 * case-insensitive SUBSTRING -- a loose containment test, not a word-boundary match, so a token also matches inside a
 * longer word. Both sides are lowercased per call rather than assuming pre-lowercased token lists, since the lists are
 * authored as readable mixed-case constants. An empty/absent haystack matches nothing.
 */
export function containsAnyTokenCaseInsensitive(haystack: string | null | undefined, tokens: readonly string[]): boolean {
	if (!haystack) return false;
	const lowered = haystack.toLowerCase();
	return tokens.some((token) => lowered.includes(token.toLowerCase()));
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): true when `haystack` contains ANY needle as a case-SENSITIVE
 * SUBSTRING -- the `needles.some((n) => haystack.includes(n))` idiom, spelled once. Twenty-three sites across
 * seventeen files carried it verbatim.
 *
 * THE THIRD MEMBER OF THIS LEAF'S DISJUNCTION-WALK FAMILY, and each member differs on exactly one axis:
 * {@link containsAnyTokenCaseInsensitive} lowercases BOTH sides per call, {@link startsWithAny} anchors at the head,
 * and this one is the free, case-SENSITIVE substring test.
 *
 * IT IS NOT A CASE-INSENSITIVE TEST WEARING A DIFFERENT NAME, and the distinction is live rather than theoretical.
 * Four carriers lowercase their haystack THEMSELVES first (`const s = haystack.toLowerCase()`) and then compare
 * against needle lists that happen to be lowercase. Those sites look interchangeable with
 * {@link containsAnyTokenCaseInsensitive} and are NOT: that one also lowercases each NEEDLE, so it would newly match
 * where a mixed-case needle previously could not. Preserving the site's own casing decision is why this member exists
 * instead of a redirect.
 *
 * DISTINCT FROM `arraysIntersect` (`@federa/const/array-guards`), which is the SAME TEXT SHAPE over a different
 * contract: there the receiver of `.includes` is an ARRAY and the question is element MEMBERSHIP, not substring
 * containment. One helper for both would be byte-invisible and behaviour-changing wherever the receiver's kind
 * changed; the two are enumerated per site, never inferred from the spelling.
 *
 * An empty `needles` matches nothing, and an empty-string needle matches ANY haystack -- both inherited from
 * `Array.prototype.some` and `String.prototype.includes` rather than chosen, and both were the behaviour at every
 * carrier.
 */
export function includesAny(haystack: string, needles: readonly string[]): boolean {
	return needles.some((needle) => haystack.includes(needle));
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic prefix scan homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. True when `value` starts with ANY member of
 * `prefixes` -- the anchored, case-SENSITIVE counterpart of {@link containsAnyTokenCaseInsensitive}, for vocabularies
 * whose members are exact leading tokens (import-specifier layer prefixes, path roots) rather than free substrings.
 * An empty `prefixes` matches nothing. Callers keep their OWN named vocabulary: this folds the disjunction walk, never
 * the member lists, which stay distinct per call site by design.
 */
export function startsWithAny(value: string, prefixes: readonly string[]): boolean {
	return prefixes.some((prefix) => value.startsWith(prefix));
}

/** @runtime-derived-ssot-helper (const-leaf-pinned): the text AFTER `prefix` when `value` starts with it, else null --
 *  a path under a root read as its root-relative tail. The rule-seam node reader and the vitest module report each
 *  spelled the startsWith-then-slice pair. */
export function textAfterPrefix(value: string, prefix: string): string | null {
	return value.startsWith(prefix) ? value.slice(prefix.length) : null;
}

/** Whitespace BETWEEN two non-space runs -- one word padded with spaces is not multi-word. */
const INNER_WHITESPACE_RE = /\S\s+\S/;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): true when `text` carries more than one word -- the test the
 * status-value scanners use to tell a PROSE literal (a sentence, a label) from an identifier-shaped enum value, which
 * never contains inner whitespace.
 */
export function isMultiWord(text: string): boolean {
	return INNER_WHITESPACE_RE.test(text);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. TOTAL and LOSSY-BY-DESIGN: every input --
 * number, boolean, object, `null`, `undefined` -- is rendered through `String()` and then TRIMMED, so absence
 * becomes the literal `'null'` / `'undefined'` rather than `''`. That is what separates it from
 * {@link stringOrEmpty}, which answers `''` for every non-string and never trims: use this one only where the
 * caller immediately length-checks the result (`id.length > 0`) and a rendered scalar id is the point.
 *
 * HOMED IN `const/` rather than on the utils floor, which (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist)
 * puts a single-export utils leaf out of reach of its own utils-layer callers -- `utils/property/id-extraction.ts`
 * composes this with two const guards and may not value-import a utils sibling.
 */
export function toSafeString(value: unknown): string {
	return String(value).trim();
}

/**
 * The COLUMN-SHAPE guard: `string | null` and nothing else -- what a nullable TEXT column must hold before a reader
 * dereferences it. The string twin of `isNullableNumber` (`@federa/const/number-guards`), minted in the same change
 * and out of the same byte-identical pair.
 *
 * IT IS NOT AN EMPTINESS TEST AND MUST NOT BE FOLDED ONTO {@link isValidString} OR {@link isNonEmptyString}, which is
 * the confusion this member exists to make impossible to make silently. `''` and a whitespace-only string both PASS
 * here, because the question is what TYPE arrived in the column: an explicit `null` is a RECORDED absence the caller
 * keeps and renders as such, while a non-empty test would collapse it onto the same verdict as an empty cell. Where
 * emptiness matters, the caller takes {@link trimToNull} or {@link nonEmptyStringOrNull} AFTER this gate.
 *
 * The two carriers this replaced differ on the CONSEQUENCE and agree on the shape, which is exactly why the shape is
 * shared and the consequence is not: `utils/knowledgebase/entry-row-guard.ts` DROPS a malformed row from both panes
 * that render the projection, while `const/standard-measure-projection.ts` THROWS a named malformed-envelope
 * error. Each keeps its own answer; neither keeps its own copy of the predicate.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): homed on the const floor because one of its two consumers is a
 * pure `utils/` leaf that may not value-import a `utils/` sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`).
 */
export function isNullableString(value: unknown): value is string | null {
	return value === null || typeof value === 'string';
}

// STRING_RENDERABLE_PRIMITIVE

/** A non-string primitive `String()` renders as its own text -- a number, bigint or boolean -- the arm both display
 *  coercers (`coerceDisplayString`, `utils/data/coerce-display-string.ts`; `toFormString`,
 *  `utils/data/coerce-form-values.ts`) render rather than collapse. A type PREDICATE, so a caller keeps the narrowing;
 *  strings, nullish values, symbols and objects stay each caller's own decision.
 *  @runtime-derived-ssot-helper (utils-floor-pinned): both coercers named above are `utils/data/` leaves that may not
 *  import a utils sibling (QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001). */
export const isStringRenderablePrimitive = (value: unknown): value is number | bigint | boolean =>
	typeof value === 'number' || typeof value === 'bigint' || typeof value === 'boolean';

/** `value` when the closed vocabulary `members` carries it, else null -- a captured string that may be unbound, narrowed
 *  to the vocabulary in one read. A registry-literal rule and an issue-reason scan each spelled the ternary by hand.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): its carriers are `tools/` leaves that may not import each other. */
export function memberOrNull(value: string | undefined, members: ReadonlySet<string>): string | null {
	return value !== undefined && members.has(value) ? value : null;
}

/** Whether ANY of the captured candidates (each possibly unbound) is in the closed vocabulary `members` -- the
 *  "named argument or composed base is a tracked binding" read two source-read rules each spelled as an `||` of
 *  guarded `has` tests. Composes {@link memberOrNull}, so the two reads cannot disagree about one candidate.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): same `tools/` carrier grounds as {@link memberOrNull}. */
export function someMember(candidates: readonly (string | undefined)[], members: ReadonlySet<string>): boolean {
	return candidates.some((candidate) => memberOrNull(candidate, members) !== null);
}
